import { useNamespaceCopy } from '@/i18n/ns';
import artifactContentCopy from '@/i18n/ns/artifact-content';
import type { DocumentPayload, ReportPayload } from '../models.ts';
import {
  BlockView,
  type RegisteredDescribe,
  type RegisteredRender,
  type ViewerProps,
} from './blocks.tsx';
import type {
  SavedArtifactView,
  SavedTextDisplay,
} from '../../../../src/artifacts/saved-view-contract.mjs';
import { SheetView } from '../spreadsheet/sheet-view';
import { SavedDeckDisplayView } from '../deck/saved-deck-view';
import { TableView } from '../table/table-view';
import { SavedChartDisplayView } from '../chart/saved-chart-view';
import './document.css';

// A document on a desk: one A4 sheet that grows with its content. Pages are the
// printer's business (the trusted print renderer cuts them); on screen a sheet
// that reflows is what a phone can read. `className`/`style` let DES2 wrap it
// while it builds, and `BlockView` is exported so a building canvas can draw a
// finished node with this same renderer, never a second one.
export function DocumentView({
  payload,
  locale,
  renderRegistered,
  describeRegistered,
  onOpenRegistered,
  className,
  style,
}: ViewerProps & {
  payload: DocumentPayload;
  renderRegistered?: RegisteredRender;
  describeRegistered?: RegisteredDescribe;
  onOpenRegistered?: (ref: string, kind: 'table' | 'chart') => void;
}) {
  const copy = useNamespaceCopy(artifactContentCopy);
  return (
    <div
      className={className ? `ws-doc-desk ${className}` : 'ws-doc-desk'}
      style={style}
      data-kind="document"
    >
      <article
        className="ws-doc-sheet"
        aria-label={payload.title || copy.documentLabel}
        data-inspect-node={payload.id}
      >
        <h1 className="ws-doc-title">{payload.title}</h1>
        {payload.blocks.map((block) => (
          <BlockView
            key={block.id}
            block={block}
            locale={locale}
            renderRegistered={renderRegistered}
            describeRegistered={describeRegistered}
            onOpenRegistered={onOpenRegistered}
          />
        ))}
      </article>
    </div>
  );
}

export function ReportView({
  payload,
  locale,
  className,
  style,
}: ViewerProps & { payload: ReportPayload }) {
  return (
    <div
      className={className ? `ws-doc-desk ${className}` : 'ws-doc-desk'}
      style={style}
      data-kind="report"
    >
      <article className="ws-doc-sheet" aria-label={payload.title} data-inspect-node={payload.id}>
        <h1 className="ws-doc-title">{payload.title}</h1>
        {payload.sections.map((section) => (
          <section key={section.id} data-inspect-node={section.id}>
            {section.title && <h2>{section.title}</h2>}
            {section.blocks.map((block) => (
              <BlockView key={block.id} block={block} locale={locale} />
            ))}
          </section>
        ))}
      </article>
    </div>
  );
}

// A saved version's display, read back through ART's public saved-view producer
// (SavedArtifactDisplay): Document and Report text with full node ids, marks, native
// presentation and the authorized values, drawn by the same BlockView as the live viewer.
// Literal sheets and Deck slides use their registered viewers. The caller owns
// the fetch, the current tuple and the stale guard.
export function SavedArtifactDisplayView({
  display,
  locale,
  className,
  style,
}: ViewerProps & { display: SavedTextDisplay }) {
  const copy = useNamespaceCopy(artifactContentCopy);
  if (display.kind === 'chart')
    return (
      <SavedChartDisplayView
        display={display}
        locale={locale}
        className={className}
        style={style}
      />
    );
  if (display.kind === 'table')
    return <TableView display={display} locale={locale} className={className} style={style} />;
  if (display.kind === 'deck') return <SavedDeckDisplayView display={display} locale={locale} />;
  if (display.kind === 'spreadsheet')
    return (
      <SheetView
        payload={{ schemaVersion: 1, id: display.id, title: display.title, sheets: display.sheets }}
        locale={locale}
        className={className}
        style={style}
      />
    );

  return (
    <div
      className={className ? `ws-doc-desk ${className}` : 'ws-doc-desk'}
      style={style}
      data-kind={display.kind}
    >
      <article
        className="ws-doc-sheet"
        aria-label={display.title || (display.kind === 'report' ? undefined : copy.documentLabel)}
        data-inspect-node={display.id}
      >
        <h1 className="ws-doc-title">{display.title}</h1>
        {display.kind === 'document'
          ? display.blocks.map((block) => (
              <BlockView key={block.id} block={block} locale={locale} />
            ))
          : display.sections.map((section) => (
              <section key={section.id} data-inspect-node={section.id}>
                {section.title && <h2>{section.title}</h2>}
                {section.blocks.map((block) => (
                  <BlockView key={block.id} block={block} locale={locale} />
                ))}
              </section>
            ))}
      </article>
    </div>
  );
}

/** The whole saved view: its display, marked with the exact version it belongs to. */
export function SavedArtifactViewer({
  view,
  ...rest
}: ViewerProps & { view: Omit<SavedArtifactView, 'display'> & { display: SavedTextDisplay } }) {
  return (
    <div data-version={view.versionId} style={{ display: 'contents' }}>
      <SavedArtifactDisplayView display={view.display} {...rest} />
    </div>
  );
}
